import { useState, useCallback, FunctionComponent } from 'react';

// libs
import * as Yup from 'yup';
import { Formik } from 'formik';
import { uploadHttp } from '@Libs/http';
import uploadApi from '@Libs/api/upload';
import { useSupportAirTable, CallbackDataType } from '@Libs/utils';

// Utilities
import classnames from 'classnames';

// Context
import { useAccountContext } from '@Components/Account/Contexts';

// Components
import Thanks from '../Components/Thanks';
import FormSupport from '../Components/FormSupport';
import { useViewDetected } from '@Components/Utils/ViewDetected';

// Styles
import styles from './Support.module.scss';
import Anchor from '@Components/Utils/Anchor';
import Image from '@Components/Utils/Image';

const validationSchema = Yup.object({
    email: Yup.string().required('Vui lòng nhập email!').email('Email chưa đúng định dạng!'),
});

interface SupportProps {
    className?: string;
    onBack?: () => void;
}

interface FieldItem {
    key: string;
    name: string;
}

type TemporaryImage = {
    fileId: string;
    fileUrl: string;
    uploadResult: {
        id: string;
        imageMediaMetadata: {
            width: number;
            height: number;
            rotation: number;
        };
    };
};

const AIRTABLE_FIELDS: Array<FieldItem> = [
    { name: 'Email', key: 'email' },
    { name: 'Vấn đề', key: 'feedback' },
    { name: 'Tên người liên hệ', key: 'name' },
    { name: 'Số điện thoại', key: 'phoneNumber' },
];

const BASE_ID = 'apprV0ryUoLMqsih1';

const BASE_FORM = 'Form Góp ý - Báo lỗi';

const Support: FunctionComponent<SupportProps> = ({ className, onBack }) => {
    const { summary } = useAccountContext();
    const { deviceType } = useViewDetected();

    const { sendAirTable } = useSupportAirTable(BASE_ID);

    const [loading, setLoading] = useState<boolean>(false);
    const [isThanks, setIsThanks] = useState<boolean>(false);

    const initValue = {
        name: '',
        email: '',
        feedback: '',
        attachments: [],
        phoneNumber: summary?.consumer?.phoneNumber?.replace('+84', '0') || '',
    };

    const goBack = useCallback(() => onBack(), []);

    const backRoot = useCallback(() => {
        goBack();
    }, []);

    const callback = (data: CallbackDataType) => {
        setLoading(false);
        if (data.type === 'success') {
            setIsThanks(true);
        }
    };

    const handleUploadImages = (uploadedAttachments) => {
        if (uploadedAttachments.length === 0) return [];

        return Promise.all(
            uploadedAttachments.map(async (value) => {
                const result: TemporaryImage = await uploadApi(uploadHttp).uploadTemporaryImage(value.id, value.file);

                return { url: result?.fileUrl };
            }),
        );
    };

    const handleSubmit = async (values) => {
        setLoading(true);
        const attachmentSubmit = await handleUploadImages(values.attachments);

        const fields = AIRTABLE_FIELDS.reduce((fieldObj, fieldItem: FieldItem) => {
            fieldObj[fieldItem.name] = values[fieldItem.key];
            return fieldObj;
        }, {});

        fields['Đính kèm'] = attachmentSubmit;

        sendAirTable(BASE_FORM, fields, '', callback);
    };

    const wrapperClassName = classnames([
        styles.wrapper,
        styles[deviceType],
        {
            [styles.active]: isThanks,
        },
        className,
    ]);

    return (
        <div className={wrapperClassName}>
            <div className={styles.form_wrapper}>
                <div className={styles.head}>
                    <span className={styles.back} onClick={goBack} />
                </div>

                <Formik initialValues={initValue} onSubmit={handleSubmit} validationSchema={validationSchema}>
                    <div className={styles.form}>
                        <div className={styles.title}>Trung tâm hỗ trợ</div>
                        <div className={styles.subtitle}>
                            Chúng tôi luôn nỗ lực cải thiện trải nghiệm
                            <br />
                            <b className="edoctor">eDoctor</b>,
                            <br />
                            vì vậy chúng tôi muốn biết làm thế nào để hiệu
                            <br />
                            quả và chúng tôi có thể làm gì để tốt hơn.
                            <br />
                            <br />
                            Trong trường hợp bạn có câu hỏi hoặc cần trợ
                            <br />
                            giúp gấp để giải quyết sự cố, bạn có thể liên hệ
                            <br />
                            chúng tôi ngay qua <b>Hotline</b>{' '}
                            <a className="edoctor" href="tel:19006115">
                                1900 6115
                            </a>
                        </div>
                        <FormSupport loading={loading} />
                    </div>
                </Formik>
            </div>
            <div className={styles.thank_you}>
                <span className={styles.back} onClick={backRoot} />
                <Thanks
                    className={styles.thanks}
                    title="Xin chân thành cảm ơn!"
                    subtitle={
                        <>
                            Cảm ơn những ý kiến phản hồi và đóng góp vô
                            <br />
                            cùng quý báu của bạn để giúp <b className="edoctor">eDoctor</b>
                            <br />
                            ngày càng hoàn thiện hơn.
                        </>
                    }
                >
                    <div className={styles.download_app}>
                        <span>Trải nghiệm ngay ứng dụng eDoctor</span>
                        <Anchor href={'https://apps.apple.com/vn/app/edoctor-know-your-health/id1041327503'} className={styles.icon_download} tracking={{ action: 'user_click_download' }}>
                            <Image alt="eDoctor - App Store" src={'/assets/downloadApp/app_store_black.png'} width={120} height={40} />
                        </Anchor>

                        <Anchor href={'https://play.google.com/store/apps/details?id=vn.edoctor.userapp'} className={styles.icon_download} tracking={{ action: 'user_click_download' }}>
                            <Image alt="eDoctor - Google Play" src={'/assets/downloadApp/google_play_black.png'} width={120} height={40} />
                        </Anchor>
                    </div>
                </Thanks>
            </div>
        </div>
    );
};

Support.defaultProps = {
    onBack: () => null,
};

export default Support;
